"use client"

import { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { IconCalendar } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPageShell, ApolloPageHeader } from "@/components/ui/apollo-page"
import {
  StatsMetaline,
  type StatsMetalineItem,
} from "@/components/ui/stats-metaline"
import { Button } from "@/components/ui/button"
import { MyTasksWidget } from "@/components/dashboard/my-tasks-widget"
import { TodayScheduleTimeline } from "@/components/dashboard/today-schedule-timeline"
import { NextPatientCard } from "@/components/dashboard/variants/doctor/next-patient-card"
import { WeekOverviewStrip } from "@/components/dashboard/variants/doctor/week-overview-strip"
import { LabOrdersInFlight } from "@/components/dashboard/variants/doctor/lab-orders-inflight"
import { MyTreatmentsPanel } from "@/components/dashboard/variants/doctor/my-treatments-panel"
import { DoctorQuickLinks } from "@/components/dashboard/variants/doctor/doctor-quick-links"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"

import { getCalendarEvents } from "@/lib/calendar-events"
import { getInFlightLabOrders } from "@/lib/lab-orders-api"
import type { LabOrder } from "@/types"
import type { CalendarEvent } from "@/components/calendar/calendar-types"

/** Local `YYYY-MM-DDTHH:mm:ss` — no timezone suffix, matching the calendar page. */
function localStamp(date: Date, time: string): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}T${time}`
}

/** Monday 00:00 of the week containing `date` (ISO week, local time). */
function startOfWeekMonday(date: Date): Date {
  const start = new Date(date)
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7))
  start.setHours(0, 0, 0, 0)
  return start
}

function byStart(a: CalendarEvent, b: CalendarEvent): number {
  return new Date(a.start).getTime() - new Date(b.start).getTime()
}

interface DoctorData {
  todayEvents: CalendarEvent[]
  weekEvents: CalendarEvent[]
  labOrders: LabOrder[]
  labTotal: number
}

const EMPTY_DATA: DoctorData = {
  todayEvents: [],
  weekEvents: [],
  labOrders: [],
  labTotal: 0,
}

/**
 * Doctor dashboard — "My day".
 *
 * Bespoke by design: a clinician does not need the clinic-wide lead/quote
 * funnel, so the generic stats band is replaced by a slim PERSONAL KPI strip
 * (today's load, next patient, lab work in flight, the week ahead) sitting over
 * three rows: next-patient + today's timeline, the Mon→Sun week band, and the
 * lab rail + shortcuts.
 *
 * Fetching contract:
 *   • Nothing fires until `usePermissions().isLoaded` — no 403 during hydration.
 *   • Calendar windows (today + this week) need `appointments.view`; the lab
 *     page needs `lab_orders.view`. An ungated source is never requested and
 *     its widget/KPI tile is OMITTED (hide-not-disable), never rendered empty.
 *   • The today-window response feeds BOTH the next-patient card and its KPI
 *     tiles — one request, two consumers. `TodayScheduleTimeline` self-fetches
 *     the same window by contract; that is the shared component's own concern.
 *   • `user_id` is passed explicitly for clarity even though the backend
 *     force-scopes a doctor's calendar feed to themselves (DOC-04).
 */
export function DoctorDashboard() {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  const { isLoaded, can } = usePermissions()
  const authUser = useAuthStore((state) => state.user)
  const myId = useAuthStore((state) => state.user?.id)

  const canViewAppointments = can("appointments.view")
  const canViewLabOrders = can("lab_orders.view")
  const canViewKartele = can("kartele.view")
  // Phase 89 — the doctor's own treatment worklist; only where lines are
  // assigned to doctors, and only for someone who can open the kartele.
  const assignmentEnabled =
    authUser?.feature_flags?.treatment_assignment_enabled ?? false
  const showMyTreatments = assignmentEnabled && canViewKartele

  const [data, setData] = useState<DoctorData>(EMPTY_DATA)
  const [isLoading, setIsLoading] = useState(true)
  // "Now" is stamped alongside the payload rather than read during render —
  // the clock is impure, and re-reading it per render would make the derived
  // next-patient pick jitter between renders (react-hooks/purity).
  const [now, setNow] = useState<number | null>(null)

  // Window anchors are pinned for the component's lifetime so the effect below
  // has stable deps (a fresh `new Date()` per render would re-fetch forever).
  const range = useMemo(() => {
    const today = new Date()
    const weekStart = startOfWeekMonday(today)
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekStart.getDate() + 6)
    return {
      weekStartDate: weekStart,
      todayStart: localStamp(today, "00:00:00"),
      todayEnd: localStamp(today, "23:59:59"),
      weekStart: localStamp(weekStart, "00:00:00"),
      weekEnd: localStamp(weekEnd, "23:59:59"),
    }
  }, [])

  useEffect(() => {
    if (!isLoaded) return

    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const [today, week, lab] = await Promise.all([
          canViewAppointments
            ? getCalendarEvents(range.todayStart, range.todayEnd, myId).catch(
                () => null,
              )
            : Promise.resolve(null),
          canViewAppointments
            ? getCalendarEvents(range.weekStart, range.weekEnd, myId).catch(
                () => null,
              )
            : Promise.resolve(null),
          // No `scope`: the API's `mine` filter keys on assigned_technician_id
          // (the lab's queue) — a dentist is the dentist_id, so scoping here
          // would always return nothing.
          canViewLabOrders
            ? getInFlightLabOrders(5).catch(() => null)
            : Promise.resolve(null),
        ])
        if (!cancelled) {
          setNow(Date.now())
          setData({
            todayEvents: today?.data ?? [],
            weekEvents: week?.data ?? [],
            labOrders: lab?.data ?? [],
            labTotal: lab?.meta.total ?? 0,
          })
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [isLoaded, canViewAppointments, canViewLabOrders, myId, range])

  // Cancelled slots are noise on a clinician's day — drop them everywhere.
  const todayActive = useMemo(
    () => data.todayEvents.filter((e) => e.status !== "cancelled").sort(byStart),
    [data.todayEvents],
  )
  const weekActive = useMemo(
    () => data.weekEvents.filter((e) => e.status !== "cancelled"),
    [data.weekEvents],
  )

  const { nextEvent, remainingCount } = useMemo(() => {
    if (now === null) return { nextEvent: null, remainingCount: 0 }
    const upcoming = todayActive.filter((event) => {
      const start = new Date(event.start).getTime()
      return !Number.isNaN(start) && start >= now
    })
    return { nextEvent: upcoming[0] ?? null, remainingCount: upcoming.length }
  }, [todayActive, now])

  const kpiItems = useMemo<StatsMetalineItem[]>(() => {
    const items: StatsMetalineItem[] = []
    // The shared strip has no loading state of its own — em-dash the figures
    // rather than flashing a hard zero that then jumps to the real count.
    const PLACEHOLDER = "—"

    if (canViewAppointments) {
      items.push({
        key: "today",
        label: t("doctorKpi.appointmentsToday"),
        value: isLoading ? PLACEHOLDER : todayActive.length,
        sub: isLoading
          ? undefined
          : t("doctorKpi.remainingToday", { count: remainingCount }),
      })
      items.push({
        key: "next",
        label: t("doctorKpi.nextPatient"),
        value:
          !isLoading && nextEvent
            ? formatter.dateTime(new Date(nextEvent.start), {
                hour: "2-digit",
                minute: "2-digit",
              })
            : PLACEHOLDER,
        sub: isLoading
          ? undefined
          : nextEvent
            ? nextEvent.lead?.full_name || nextEvent.title
            : t("doctorKpi.dayComplete"),
      })
    }

    if (canViewLabOrders) {
      items.push({
        key: "lab",
        label: t("doctorKpi.labInFlight"),
        value: isLoading ? PLACEHOLDER : data.labTotal,
        sub: isLoading ? undefined : t("doctorKpi.labInFlightSub"),
      })
    }

    if (canViewAppointments) {
      items.push({
        key: "week",
        label: t("doctorKpi.thisWeek"),
        value: isLoading ? PLACEHOLDER : weekActive.length,
        sub: isLoading ? undefined : t("doctorKpi.thisWeekSub"),
      })
    }

    return items
  }, [
    canViewAppointments,
    canViewLabOrders,
    isLoading,
    todayActive,
    weekActive,
    nextEvent,
    remainingCount,
    data.labTotal,
    formatter,
    t,
  ])

  const showQuickLinks = canViewAppointments || canViewKartele

  return (
    <ApolloPageShell>
      <ApolloPageHeader
        eyebrow={t("page.eyebrow")}
        title={t("variants.doctor.title")}
        stats={kpiItems.length > 0 ? <StatsMetaline items={kpiItems} /> : undefined}
      />

      {/* Doctor welcome — slim accent band (DOC-17), ported from the pre-split page. */}
      {authUser && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-accent-tint bg-accent-tint-2 px-4 py-2.5 md:px-6">
          <span className="text-sm font-medium text-foreground">
            {t("page.doctorWelcome", { name: authUser.last_name ?? "" })}
          </span>
          <span className="text-sm text-text-dim">{t("page.doctorScheduleHint")}</span>
          <Button asChild size="sm" className="ml-auto">
            <Link href="/calendar" className="flex items-center gap-2">
              <IconCalendar className="h-4 w-4" />
              {t("page.todaySchedule")}
            </Link>
          </Button>
        </div>
      )}

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
          {/* My Tasks — self-gating (tasks_enabled + tasks.view.assigned). */}
          <MyTasksWidget />

          {/* Row 1 — next patient (1fr) + today's timeline (2fr) */}
          {canViewAppointments && (
            <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3">
              <NextPatientCard
                event={nextEvent}
                remainingCount={remainingCount}
                isLoading={isLoading}
              />
              <TodayScheduleTimeline userId={myId} className="lg:col-span-2" />
            </div>
          )}

          {/* Row 2 — Mon→Sun week band */}
          {canViewAppointments && (
            <WeekOverviewStrip
              weekStart={range.weekStartDate}
              events={weekActive}
              isLoading={isLoading}
            />
          )}

          {/* Row 2b — Phase 89: my treatments, soonest pending step first */}
          {showMyTreatments && <MyTreatmentsPanel />}

          {/* Row 3 — lab rail (2fr) + shortcuts (1fr) */}
          {(canViewLabOrders || showQuickLinks) && (
            <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3">
              {canViewLabOrders && (
                <LabOrdersInFlight
                  orders={data.labOrders}
                  total={data.labTotal}
                  isLoading={isLoading}
                  className="lg:col-span-2"
                />
              )}
              {showQuickLinks && (
                <DoctorQuickLinks
                  canViewCalendar={canViewAppointments}
                  canViewKartele={canViewKartele}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </ApolloPageShell>
  )
}
